import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaidFees, generateReceipt } from "../../redux/admin/fees.thunk";
import { downloadAdminReceipt } from "../../api/admin/fees.api";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const PaidFees = () => {
  const dispatch = useDispatch();

  /* ================= REDUX STATE ================= */
  const {
    paidFees = [],
    paidFeesCurrentPage = 1,
    paidFeesTotalPages = 1,
    paidFeesTotalRecords = 0,
    loading = false,
  } = useSelector((state) => state.admin?.fees || {});

  const pagination = {
    current_page: paidFeesCurrentPage,
    total_pages: paidFeesTotalPages,
    total_records: paidFeesTotalRecords,
  };

  /* ================= LOCAL STATE ================= */
  const [processingId, setProcessingId] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  /* ================= DEBOUNCE SEARCH ================= */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  /* ================= LOAD DATA ================= */
  const loadData = useCallback(
    (page = 1) => {
      dispatch(
        fetchPaidFees({
          page,
          limit: 10,
          search: debouncedSearch,
        })
      );
    },
    [dispatch, debouncedSearch]
  );

  useEffect(() => {
    loadData(1);
  }, [loadData]);

  /* ================= HANDLERS ================= */
  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.total_pages) return;
    loadData(page);
  };

  const handleGenerateReceipt = async (id) => {
    try {
      setProcessingId(id);
      await dispatch(generateReceipt(id)).unwrap();
      toastr.success("Receipt generated successfully");
      loadData(pagination.current_page);
    } catch (error) {
      toastr.error(error || "Failed to generate receipt");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDownloadReceipt = async (id) => {
    try {
      await downloadAdminReceipt(id);
      toastr.success("Download started");
    } catch (error) {
      toastr.error("Download failed");
    }
  };

  return (
    <div className="precise-app-bg">
      <div className="precise-card">
        {/* HEADER */}
        <header className="precise-header">
          <div className="title-area">
            <span className="meta-tag">Finance Management</span>
            <h1>Paid Fee Records</h1>
            <p>Review student payments and generate official digital receipts</p>
          </div>
        </header>

        {/* TOOLBAR */}
        <section className="precise-toolbar">
          <div className="search-wrapper">
            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search student or admission no..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-wrapper">
            <span className="stats-pill">
              {pagination.total_records} Total Records
            </span>
          </div>
        </section>

        {/* TABLE */}
        <div className="table-responsive">
          <table className="precise-table">
            <thead>
              <tr>
                <th>Student Details</th>
                <th>Fee Details</th>
                <th>Payment Context</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="state-msg">
                    <div className="spinner-border spinner-border-sm me-3 text-primary"></div>
                    Fetching records...
                  </td>
                </tr>
              ) : paidFees.length === 0 ? (
                <tr>
                  <td colSpan="4" className="state-msg">
                    {debouncedSearch ? `No results found for "${debouncedSearch}"` : "No paid fee records available"}
                  </td>
                </tr>
              ) : (
                paidFees.map((fee) => (
                  <tr key={fee.id} className="precise-row">
                    <td>
                      <div className="user-info">
                        <div className="user-icon">{fee.name?.charAt(0)}</div>
                        <div>
                          <span className="u-name">{fee.name}</span>
                          <span className="u-id">ID: {fee.admission_number || fee.student_id}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="fee-amount">₹{Number(fee.amount).toLocaleString()}</span>
                      <span className="u-id">{fee.month_year}</span>
                    </td>
                    <td>
                      <span className="pay-mode">
                        {fee.payment_mode?.toUpperCase() || "CASH"}
                      </span>
                      <span className="u-id">Paid on {fee.payment_date}</span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {fee.receipt_generated ? (
                        <button className="btn-download-receipt" onClick={() => handleDownloadReceipt(fee.id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '6px'}}>
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                          Download
                        </button>
                      ) : (
                        <button
                          className="btn-generate-receipt"
                          disabled={processingId === fee.id}
                          onClick={() => handleGenerateReceipt(fee.id)}
                        >
                          {processingId === fee.id ? (
                            <><span className="spinner-border spinner-border-sm me-2"></span>Wait...</>
                          ) : (
                            "Generate Receipt"
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* FOOTER */}
        <footer className="precise-footer">
          <div className="stats">
            Showing <b>{paidFees.length}</b> of <b>{pagination.total_records}</b> entries
          </div>
          <div className="pagination-group">
            <button
              className="btn-pg"
              disabled={pagination.current_page <= 1 || loading}
              onClick={() => handlePageChange(pagination.current_page - 1)}
            >
              Prev
            </button>
            <div className="pg-indicator">
              Page <span>{pagination.current_page}</span> of {pagination.total_pages}
            </div>
            <button
              className="btn-pg"
              disabled={pagination.current_page >= pagination.total_pages || loading}
              onClick={() => handlePageChange(pagination.current_page + 1)}
            >
              Next
            </button>
          </div>
        </footer>
      </div>

      <style>{`
        .precise-app-bg {
          background: #f8fafc;
          min-height: 100vh;
          padding: 2.5rem 1rem;
          font-family: 'Inter', -apple-system, sans-serif;
          color: #1e293b;
        }

        .precise-card {
          max-width: 1100px;
          margin: 0 auto;
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
          overflow: hidden;
        }

        .precise-header {
          padding: 1.75rem 2rem;
          background: #fff;
          border-bottom: 1px solid #f1f5f9;
        }

        .precise-header h1 {
          font-size: 1.25rem;
          font-weight: 800;
          margin: 0.5rem 0 0.25rem 0;
          color: #0f172a;
          letter-spacing: -0.02em;
        }

        .precise-header p {
          color: #64748b;
          font-size: 0.85rem;
          margin: 0;
        }

        .meta-tag {
          background: #eff6ff;
          color: #2563eb;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.65rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .precise-toolbar {
          padding: 1rem 2rem;
          background: #fcfdfe;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
        }

        .search-wrapper {
          position: relative;
          flex: 1;
          max-width: 360px;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          pointer-events: none;
        }

        .search-wrapper input {
          width: 100%;
          padding: 0.6rem 1rem 0.6rem 2.5rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          outline: none;
          font-size: 0.85rem;
          transition: all 0.2s ease;
          background: #ffffff;
        }

        .search-wrapper input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .stats-pill {
          background: #f1f5f9;
          color: #475569;
          padding: 0.5rem 0.9rem;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .precise-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: auto;
        }

        .precise-table th {
          background: #f8fafc;
          padding: 0.85rem 2rem;
          text-align: left;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          font-weight: 700;
          border-bottom: 1px solid #f1f5f9;
        }

        .precise-row {
          transition: background 0.15s ease;
        }

        .precise-row:hover {
          background: #f8fafc;
        }

        .precise-table td {
          padding: 1.1rem 2rem;
          border-bottom: 1px solid #f1f5f9;
          font-size: 0.85rem;
          vertical-align: middle;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .user-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: #f1f5f9;
          color: #334155;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.9rem;
          border: 1px solid #e2e8f0;
        }

        .u-name {
          display: block;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 1px;
        }

        .u-id {
          display: block;
          font-size: 0.75rem;
          color: #94a3b8;
          font-weight: 500;
        }

        .fee-amount {
          display: block;
          color: #10b981;
          font-weight: 800;
          font-size: 0.9rem;
          margin-bottom: 1px;
        }

        .pay-mode {
          display: inline-block;
          background: #f1f5f9;
          color: #475569;
          padding: 2px 8px;
          border-radius: 6px;
          font-size: 0.65rem;
          font-weight: 700;
          margin-bottom: 3px;
          border: 1px solid #e2e8f0;
        }

        .btn-generate-receipt {
          background: #6366f1;
          color: #fff;
          border: none;
          border-radius: 7px;
          padding: 0.5rem 1.1rem;
          font-size: 0.75rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-generate-receipt:hover:not(:disabled) {
          background: #4f46e5;
          transform: translateY(-1px);
        }

        .btn-download-receipt {
          background: #10b981;
          color: #fff;
          border: none;
          border-radius: 7px;
          padding: 0.5rem 1.1rem;
          font-size: 0.75rem;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          transition: all 0.2s;
        }

        .btn-download-receipt:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        .precise-footer {
          padding: 1.25rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #fff;
          border-top: 1px solid #f1f5f9;
        }

        .stats {
          font-size: 0.8rem;
          color: #64748b;
        }

        .stats b {
          color: #0f172a;
        }

        .pagination-group {
          display: flex;
          align-items: center;
          gap: 1.25rem;
        }

        .btn-pg {
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #475569;
          padding: 0.45rem 1rem;
          border-radius: 7px;
          cursor: pointer;
          font-size: 0.75rem;
          font-weight: 600;
          transition: all 0.2s;
        }

        .btn-pg:hover:not(:disabled) {
          border-color: #cbd5e1;
          background: #f8fafc;
        }

        .btn-pg:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .pg-indicator {
          font-size: 0.75rem;
          font-weight: 700;
          color: #64748b;
        }

        .pg-indicator span {
          color: #2563eb;
          background: #eff6ff;
          padding: 2px 8px;
          border-radius: 4px;
        }

        .state-msg {
          text-align: center;
          padding: 4rem !important;
          color: #94a3b8;
          font-size: 0.9rem;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default PaidFees;